// @ts-nocheck
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { SplitOrderCartNotesModel, SplitOrderModel } from "@insite/client-framework/Types/ApiModels";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import updateSplitOrderCartNotes from "@store/Framework/Store/Greif/Pages/ReviewAndSubmit/SplitOrderCartLine/Handlers/UpdateSplitOrderCartNotes";
import { CartLinesDeliveryDate } from "@store/Library/Components/Cart/CartLinesDates";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { ChangeEvent, useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    splitOrder: SplitOrderModel & Cart;
    maxLength?: number;
}

type Props = OwnProps & TextAreaProps;

export interface CheckoutSplitOrderNotesStyles {
    textArea?: TextAreaProps;
}

export const checkoutSplitOrderNotesStyles: CheckoutSplitOrderNotesStyles = {
    textArea: {
        border: "rectangle",
        css: css`
            min-width: 100%;
            max-width: 100%;
            min-height: ${pxToRem(50)};

            && {
                border-radius: ${pxToRem(4)};
            }
        `,
    },
};

const styles = checkoutSplitOrderNotesStyles;

const CheckoutSplitOrderNotes = ({ splitOrder, maxLength: orderLineMaxLength }: Props) => {
    const [splitOrderNotes, setSplitOrderNotes] = useState<string>(splitOrder.notes);
    const [notesLength, setNotesLength] = useState<number>(splitOrder.notes.length);
    const { isUpdatingNotes, failedToUpdateSplitOrderCartLine } = useSelector(
        (state: ApplicationState) => state.greif.pages.reviewAndSubmit.splitOrderCartLine,
    );
    const dispatch = useDispatch();
    const toasterContext = useContext(ToasterContext);

    const deliveryDateIds = splitOrder.lines.map(line => {
        const { deliveryDate } = line.properties;
        const deliveryDateObj: CartLinesDeliveryDate = deliveryDate ? JSON.parse(deliveryDate) : {};

        return deliveryDateObj?.id;
    });

    const notesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setSplitOrderNotes(event.currentTarget.value);
        setNotesLength(event.currentTarget.value.length);
    };

    const notesBlurHandler = (event: React.FocusEvent<HTMLTextAreaElement>) => {
        if (splitOrderNotes !== splitOrder.notes) {
            dispatch(
                updateSplitOrderCartNotes({
                    splitOrderCartNotes: {
                        cartId: splitOrder.id,
                        deliveryDateIds,
                        notes: splitOrderNotes,
                    } as SplitOrderCartNotesModel,
                    onSuccess: () => {
                        toasterContext.addToast({
                            body: translate("Line Notes Updated"),
                            messageType: "success",
                        });
                    },
                    onComplete(resultProps) {
                        if (resultProps.apiResult) {
                            // "this" is targeting the object being created, not the parent SFC
                            // eslint-disable-next-line react/no-this-in-sfc
                            this.onSuccess?.();
                        }
                        if (failedToUpdateSplitOrderCartLine) {
                            toasterContext.addToast({
                                body: translate("Notes failed to be updated"),
                                messageType: "danger",
                            });
                        }
                    },
                }),
            );
        }
    };

    const hintText =
        // When 80% of the defined value is reached, show the textarea hint with the caracter counter.
        orderLineMaxLength && notesLength > orderLineMaxLength * 0.8
            ? translate(`{0} of {1} characters`, String(notesLength), String(orderLineMaxLength))
            : null;

    return (
        <LoadingOverlay loading={isUpdatingNotes}>
            <TextArea
                {...styles.textArea}
                label={translate("Add Order Notes")}
                placeholder={translate("You can start typing here")}
                value={splitOrderNotes}
                onChange={notesChangeHandler}
                onBlur={notesBlurHandler}
                maxLength={orderLineMaxLength}
                hint={hintText}
            />
        </LoadingOverlay>
    );
};

export default CheckoutSplitOrderNotes;
